import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import SEO from '../components/SEO';
import {
  ToolGlyph,
  ToolStatus,
} from '../components/tool-store/ToolStoreUI';
import { getSupernovaTool } from '../services/supernovaToolsService';
import '../styles/pages/ToolShowcasePage.css';

const isAbsoluteUrl = (value = '') => /^https?:\/\//i.test(value);
const isSafeProjectUrl = (value = '') =>
  /^(\/(?!\/)|https?:\/\/)/i.test(value);
const supportedActionKinds = new Set(['download', 'external', 'internal']);

const ShowcaseAction = ({ action }) => {
  const content = (
    <>
      <span>{action.label}</span>
      <ToolGlyph
        name={
          action.icon ||
          (action.kind === 'download' ? 'document' : 'external')
        }
        size={18}
      />
    </>
  );

  if (action.kind === 'internal') {
    return (
      <Link className="sts-action" to={action.href}>
        {content}
      </Link>
    );
  }

  return (
    <a
      className="sts-action"
      download={action.kind === 'download' ? action.downloadName || true : undefined}
      href={action.href}
      rel={action.kind === 'external' ? 'noopener noreferrer' : undefined}
      target={action.kind === 'external' ? '_blank' : undefined}
    >
      {content}
    </a>
  );
};

const ToolShowcasePage = () => {
  const { slug } = useParams();
  const [tool, setTool] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [frameState, setFrameState] = useState('loading');

  useEffect(() => {
    const controller = new AbortController();

    const loadTool = async () => {
      setLoading(true);
      setError('');
      setFrameState('loading');

      try {
        const response = await getSupernovaTool(slug, {
          signal: controller.signal,
        });
        setTool(response.data);
      } catch (requestError) {
        if (requestError.name !== 'AbortError') {
          setError('This project could not be loaded.');
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    loadTool();
    return () => controller.abort();
  }, [slug]);

  const showcase = tool?.showcase;
  const embed = showcase?.embed;
  const embedSource = isSafeProjectUrl(embed?.src) ? embed.src : null;
  const actions = (showcase?.actions || []).filter(
    (action) =>
      supportedActionKinds.has(action.kind) &&
      isSafeProjectUrl(action.href) &&
      action.id &&
      action.label,
  );

  const iframeStyle = useMemo(
    () => ({
      '--sts-embed-height': `${embed?.height || 720}px`,
    }),
    [embed?.height],
  );

  if (loading) {
    return (
      <main className="sts-page-state" id="tool-showcase-main">
        <span className="sts-loader" aria-hidden="true" />
        <p>Preparing the project…</p>
      </main>
    );
  }

  if (error) {
    return (
      <main className="sts-page-state" id="tool-showcase-main" role="alert">
        <ToolGlyph name="info" size={32} />
        <h1>We could not open this project</h1>
        <p>{error}</p>
        <Link to="/supernova-tools">Return to all tools</Link>
      </main>
    );
  }

  if (!tool) {
    return (
      <main className="sts-page-state" id="tool-showcase-main">
        <ToolGlyph name="search" size={32} />
        <h1>Project not found</h1>
        <p>The requested project is not part of the current collection.</p>
        <Link to="/supernova-tools">Browse Supernova Tools</Link>
      </main>
    );
  }

  return (
    <>
      <SEO
        description={tool.longDescription}
        title={`${tool.title} | Supernova Tools`}
      />

      <main className="tool-showcase-page" id="tool-showcase-main">
        <a className="sts-skip-link" href="#interactive-experience">
          Skip to interactive experience
        </a>

        <div className="sts-shell">
          <Link className="sts-back-link" to="/supernova-tools">
            <ToolGlyph name="back" size={20} />
            All tools
          </Link>

          <header className="sts-project-header">
            <div className="sts-project-heading">
              <h1>{tool.title}</h1>
              <p>{tool.description}</p>
            </div>

            <dl className="sts-project-meta">
              <div>
                <dt>Type</dt>
                <dd>
                  <span className="snt-type-pill">{tool.type}</span>
                </dd>
              </div>
              <div>
                <dt>Availability</dt>
                <dd>
                  <ToolStatus tool={tool} />
                </dd>
              </div>
            </dl>
          </header>

          <section className="sts-about" aria-labelledby="project-about-title">
            <h2 id="project-about-title">About</h2>
            <p>{showcase?.about || tool.longDescription}</p>
          </section>

          <section
            className="sts-experience"
            id="interactive-experience"
            aria-labelledby="interactive-experience-title"
          >
            <div className="sts-experience-heading">
              <div>
                <h2 id="interactive-experience-title">
                  Interactive experience
                </h2>
                <p>
                  Preview the project directly below or open it in a separate
                  tab.
                </p>
              </div>

              {embedSource ? (
                <a
                  href={embedSource}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  Open full screen
                  <ToolGlyph name="external" size={17} />
                </a>
              ) : null}
            </div>

            {embedSource ? (
              <div
                className={`sts-embed sts-embed--${frameState}`}
                style={iframeStyle}
              >
                {frameState === 'loading' ? (
                  <div className="sts-embed-status" role="status">
                    <span className="sts-loader" aria-hidden="true" />
                    <p>Loading the interactive experience…</p>
                  </div>
                ) : null}

                <iframe
                  allow={embed.allow}
                  loading="lazy"
                  onError={() => setFrameState('error')}
                  onLoad={() => setFrameState('ready')}
                  referrerPolicy="strict-origin-when-cross-origin"
                  sandbox={embed.sandbox}
                  src={embedSource}
                  title={embed.title}
                />
              </div>
            ) : (
              <div className="sts-embed-empty">
                <ToolGlyph name="lock" size={28} />
                <h3>Interactive experience coming soon</h3>
                <p>This project is not available to use yet.</p>
              </div>
            )}
          </section>

          {frameState === 'error' ? (
            <div className="sts-embed-error" role="alert">
              <ToolGlyph name="info" size={20} />
              <p>
                The embedded experience could not be displayed. Use an action
                below to continue.
              </p>
            </div>
          ) : null}

          {actions.length ? (
            <nav className="sts-actions" aria-label={`${tool.title} actions`}>
              {actions.map((action) => (
                <ShowcaseAction action={action} key={action.id} />
              ))}
            </nav>
          ) : null}

          {embedSource && isAbsoluteUrl(embedSource) ? (
            <p className="sts-external-note">
              This experience is provided by an external project. Its privacy
              and data practices may differ from Supernova Lab.
            </p>
          ) : null}
        </div>
      </main>
    </>
  );
};

export default ToolShowcasePage;
